import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useLang } from 'hooks/lang';

import { Button, Drawer, IconButton, Input, ResponsiveRender } from 'components/Blocks';
import { SearchIcon, XIcon } from 'components/Icons';

import { useToggle } from 'hooks';
import { useDebounce } from 'hooks/debounce';

import { cn } from 'styles';

interface SearchProjectProps {
  children?: never;
  term: string;
  updateTerm: (term: string | null) => void;
}

export function SearchProject({ term, updateTerm }: SearchProjectProps) {
  const { isOn, turnOff, turnOn } = useToggle();

  const inputRef = useRef<HTMLInputElement>(null);

  const [localValue, setLocalValue] = useState(term);
  const debouncedSearch = useDebounce(localValue, 300);

  const { t } = useLang();

  useEffect(() => {
    updateTerm(debouncedSearch || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleUpdate = useCallback(({ target }: ChangeEvent<HTMLInputElement>) => {
    setLocalValue(target.value);
  }, []);

  const open = () => {
    turnOn();
    inputRef.current?.focus();
  };

  const cancel = () => {
    updateTerm('');
    setLocalValue('');
    inputRef.current?.focus();
  };

  const isOpen = !!term || isOn;

  return (
    <ResponsiveRender
      defaultContent={
        <div className="overflow-hidden">
          <div className={cn('relative flex h-9 items-center rounded-lg', isOpen && 'border px-2')}>
            <IconButton
              className={isOpen ? 'pointer-events-none cursor-default' : ''}
              onClick={isOpen ? undefined : open}
            >
              <SearchIcon />
            </IconButton>

            <input
              className={cn(
                'w-0 rounded-sm bg-transparent py-4 outline-0 transition-width',
                isOpen && 'w-[240px] px-2',
              )}
              onFocus={turnOn}
              onBlur={term ? undefined : turnOff}
              value={localValue || ''}
              ref={inputRef}
              onChange={handleUpdate}
            />

            {term && (
              <IconButton
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0"
                onClick={cancel}
              >
                <XIcon />
              </IconButton>
            )}
          </div>
        </div>
      }
      responsiveContent={
        <>
          <IconButton onClick={open} className={cn(term && 'text-primary')}>
            <SearchIcon />
          </IconButton>

          <Drawer
            title="Project Search"
            isOpen={isOn}
            close={turnOff}
            enforceDrawerMinHeight={false}
          >
            <Input value={localValue || ''} ref={inputRef} onChange={handleUpdate} />

            {term && (
              <Button variant="outlined" onClick={cancel}>
                {t('clear')}
              </Button>
            )}

            <Button onClick={turnOff}>{t('close')}</Button>
          </Drawer>
        </>
      }
    />
  );
}
